import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatCompact,
  formatDelta,
  formatMetricValue,
  formatNumber,
  formatShare,
  type DeltaKind,
  type FormatMetricOptions,
  type MetricValueKind,
} from '../utils/numberFormat';

/**
 * Binds the display number formatters to the active UI language and resolves
 * unit keys, e.g. `fmt.metric('tco2e', 12.34)` → `{ text: '12.34', unit: 'tCO2e' }`.
 */
export function useNumberFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return useMemo(
    () => ({
      lang,
      number: (value: number | null | undefined, options?: Intl.NumberFormatOptions) =>
        formatNumber(value, lang, options),
      compact: (value: number | null | undefined) => formatCompact(value, lang),
      share: (fraction: number | null | undefined) => formatShare(fraction, lang),
      delta: (value: number | null | undefined, kind: DeltaKind) => formatDelta(value, kind, lang),
      metric: (kind: MetricValueKind, value: number | null | undefined, options?: FormatMetricOptions) => {
        const formatted = formatMetricValue(kind, value, lang, options);
        return {
          ...formatted,
          unit: formatted.unitKey ? t(formatted.unitKey) : '',
        };
      },
      /** Figure + unit as one string, e.g. "12.3 tCO2e" (for tables / tooltips). */
      withUnit: (kind: MetricValueKind, value: number | null | undefined, options?: FormatMetricOptions) => {
        const formatted = formatMetricValue(kind, value, lang, options);
        if (formatted.empty || !formatted.unitKey) return formatted.text;
        const unit = t(formatted.unitKey);
        return kind === 'percent' ? `${formatted.text}${unit}` : `${formatted.text} ${unit}`;
      },
    }),
    [lang, t],
  );
}

export type NumberFormatApi = ReturnType<typeof useNumberFormat>;
